import JsPDF from 'jspdf';
import moment from 'moment';
import { PdfPage } from 'components/PdfExportConfigDialog/hooks/types';
import { extractDate, ISO_DATE_FORMAT, Logger } from 'utilities';
import {
  CONVERSION_RATIOS,
  IMAGE_SETTINGS,
  TEMPLATE_CONSTANTS,
} from './constants';
import { CapturedImage, PdfExportConfig, PdfExportResult } from './interfaces';

/**
 * PDF Exporter class for handling multiple pages export
 */
export class PdfExporter {
  private pdf: JsPDF | null = null;
  private pageLogo: string | null = null;
  private coverLogoDataUrl: string | null = null;
  private infoIcon: string | null = null;
  private footerLogo: string | null = null;

  /**
   * Static cache to store image data URLs by their source path.
   * Prevents redundant network requests and base64 conversions.
   */
  private static imageCache: Map<string, Promise<string>> = new Map();

  // Streaming state. The capture pipeline calls initDocument() once, then
  // addCapturedPage() for each captured page (in input order), then finalize().
  // Footer rendering is deferred to finalize() because it needs the final
  // included-page count for the "Page X of Y" stamp.
  private renderConfig: PdfExportConfig | null = null;
  private addedPages: { link: PdfPage }[] = [];

  /**
   * Load image from public folder and convert to base64 data URL
   */
  private static async loadImageAsDataUrl(imagePath: string): Promise<string> {
    if (this.imageCache.has(imagePath)) {
      return this.imageCache.get(imagePath)!;
    }

    const promise = (async () => {
      try {
        const response = await fetch(imagePath);
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} fetching ${imagePath}`);
        }
        const blob = await response.blob();
        return await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      } catch (error) {
        Logger.error('Error loading image:', error);
        // Remove from cache on failure so we can retry later
        this.imageCache.delete(imagePath);
        throw error;
      }
    })();

    this.imageCache.set(imagePath, promise);
    return promise;
  }

  /**
   * Infer jspdf-compatible format string ('png' | 'jpeg') from a data URL or
   * file path. Falls back to PNG for unknown/unrecognized inputs.
   */
  private static getImageFormat(src: string): typeof IMAGE_SETTINGS.PNG_FORMAT | typeof IMAGE_SETTINGS.JPEG_FORMAT {
    if (src.startsWith('data:image/jpeg') || src.startsWith('data:image/jpg')) {
      return IMAGE_SETTINGS.JPEG_FORMAT;
    }
    if (/\.(jpe?g)(\?|$)/i.test(src)) {
      return IMAGE_SETTINGS.JPEG_FORMAT;
    }
    return IMAGE_SETTINGS.PNG_FORMAT;
  }

  /**
   * Get image dimensions from data URL
   */
  private static async getImageDimensions(dataUrl: string): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.naturalWidth, height: img.naturalHeight });
      };
      img.onerror = reject;
      img.src = dataUrl;
    });
  }

  /**
   * Convert Blob to binary string for jsPDF
   */
  private static blobToBinaryString(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const arrayBuffer = reader.result as ArrayBuffer;
        const bytes = new Uint8Array(arrayBuffer);
        let binary = '';
        for (let i = 0; i < bytes.byteLength; i++) {
          binary += String.fromCharCode(bytes[i]);
        }
        resolve(binary);
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(blob);
    });
  }

  private static async loadCustomFonts(pdf: JsPDF): Promise<void> {
    try {
      // Load Inter Regular
      const interRegularResponse = await fetch('/fonts/Inter-Regular.ttf');
      if (!interRegularResponse.ok) {
        throw new Error(`Failed to load Inter-Regular.ttf: ${interRegularResponse.statusText}`);
      }
      const interRegularBlob = await interRegularResponse.blob();
      const interRegularBinary = await PdfExporter.blobToBinaryString(interRegularBlob);

      pdf.addFileToVFS('Inter-Regular.ttf', interRegularBinary);
      pdf.addFont('Inter-Regular.ttf', 'Inter', 'normal');

      // Load Inter Italic
      const interItalicResponse = await fetch('/fonts/Inter-Italic.ttf');
      if (!interItalicResponse.ok) {
        throw new Error(`Failed to load Inter-Italic.ttf: ${interItalicResponse.statusText}`);
      }
      const interItalicBlob = await interItalicResponse.blob();
      const interItalicBinary = await PdfExporter.blobToBinaryString(interItalicBlob);

      pdf.addFileToVFS('Inter-Italic.ttf', interItalicBinary);
      pdf.addFont('Inter-Italic.ttf', 'Inter', 'italic');

      // Load Inter ExtraLight
      const interExtraLightResponse = await fetch('/fonts/Inter-ExtraLight.ttf');
      if (!interExtraLightResponse.ok) {
        throw new Error(`Failed to load Inter-ExtraLight.ttf: ${interExtraLightResponse.statusText}`);
      }
      const interExtraLightBlob = await interExtraLightResponse.blob();
      const interExtraLightBinary = await PdfExporter.blobToBinaryString(interExtraLightBlob);
      pdf.addFileToVFS('Inter-ExtraLight.ttf', interExtraLightBinary);
      pdf.addFont('Inter-ExtraLight.ttf', 'Inter', 'extra-light');

      // Load Inter Bold
      const interBoldResponse = await fetch('/fonts/Inter-Bold.ttf');
      if (interBoldResponse.ok) {
        const interBoldBlob = await interBoldResponse.blob();
        const interBoldBinary = await PdfExporter.blobToBinaryString(interBoldBlob);
        pdf.addFileToVFS('Inter-Bold.ttf', interBoldBinary);
        pdf.addFont('Inter-Bold.ttf', 'Inter', 'bold');
      }

      // Load Neuton Light
      const neutonResponse = await fetch('/fonts/Neuton-Light.ttf');
      if (!neutonResponse.ok) {
        throw new Error(`Failed to load Neuton-Light.ttf: ${neutonResponse.statusText}`);
      }
      const neutonBlob = await neutonResponse.blob();
      const neutonBinary = await PdfExporter.blobToBinaryString(neutonBlob);
      pdf.addFileToVFS('Neuton-Light.ttf', neutonBinary);
      pdf.addFont('Neuton-Light.ttf', 'Neuton', 'light');

      // Load Neuton Italic
      const neutonItalicResponse = await fetch('/fonts/Neuton-Italic.ttf');
      if (!neutonItalicResponse.ok) {
        throw new Error(`Failed to load Neuton-Italic.ttf: ${neutonItalicResponse.statusText}`);
      }
      const neutonItalicBlob = await neutonItalicResponse.blob();
      const neutonItalicBinary = await PdfExporter.blobToBinaryString(neutonItalicBlob);
      pdf.addFileToVFS('Neuton-Italic.ttf', neutonItalicBinary);
      pdf.addFont('Neuton-Italic.ttf', 'Neuton', 'italic');

      Logger.info('Custom fonts loaded successfully');
    } catch (error) {
      Logger.warn('Failed to load custom fonts', error);
    }
  }

  /**
   * Generate cover page template
   */
  public async generateCover(config: PdfExportConfig): Promise<void> {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const {
      coverBackgroundImage,
      coverTitle,
      entityName,
      dateOfIssue,
      coverLogo,
      coverSecondaryLogo,
      isFirmOwned,
      measurementDate,
    } = config;

    // Background image
    this.pdf.addImage(
      coverBackgroundImage,
      IMAGE_SETTINGS.JPEG_FORMAT,
      0,
      0,
      this.pdf.internal.pageSize.getWidth(),
      this.pdf.internal.pageSize.getHeight()
    );

    // Use the pre-loaded data URL so jspdf receives the decoded bytes; detect
    // the actual format to avoid the 'wrong PNG signature' crash when the firm
    // logo is a JPEG. Never fall back to a raw URL/path — jspdf cannot fetch
    // external URLs and will throw "wrong PNG signature" on the raw string bytes.
    const coverLogoSrc = this.coverLogoDataUrl;
    const coverLogoFormat = coverLogoSrc ? PdfExporter.getImageFormat(coverLogoSrc) : IMAGE_SETTINGS.PNG_FORMAT;

    // Logo image setup — only measure dimensions when we have a valid data URL.
    const logoHeightMm = coverLogoSrc
      ? (await PdfExporter.getImageDimensions(coverLogoSrc)).height / CONVERSION_RATIOS.MM_TO_PX
      : 0;
    const pageHeight = this.pdf.internal.pageSize.getHeight();
    const pageWidth = this.pdf.internal.pageSize.getWidth();

    if (isFirmOwned) {
      // Top left position — only rendered when the logo loaded successfully as a data URL.
      if (coverLogoSrc) {
        this.pdf.addImage(
          coverLogoSrc,
          coverLogoFormat,
          TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
          TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM,
          TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM,
          TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM
        );
      }

      // Draw a horizontal line to the right of the logo, centered vertically, spanning to page margin
      const logoCenterY = TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM + TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM / 2;
      const lineXStart =
        TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM +
        TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM +
        TEMPLATE_CONSTANTS.COVER_LINE_LEFT_MARGIN_MM;
      const lineXEnd = pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(3, 125, 232);
      this.pdf.line(lineXStart, logoCenterY, lineXEnd, logoCenterY);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);

      // Bottom right position: Line + Space + "Powered by Scalar" + Space + Logo
      const logoBottomRightCenterY =
        pageHeight -
        TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM -
        TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM / 2;

      // Logo position (rightmost)
      const logoBottomRightX =
        pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM - TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_WIDTH_MM;
      const logoBottomRightY =
        pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM;

      // Text position (before logo)
      this.pdf.setFont('Inter', 'extra-light');
      this.pdf.setFontSize(10);
      const textWidth = this.pdf.getTextWidth('Powered by');
      const textX = logoBottomRightX - TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_TEXT_SPACING_MM - textWidth;

      // Line position (before text)
      const lineBottomRightXStart = TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      const lineBottomRightXEnd = textX - TEMPLATE_CONSTANTS.COVER_SECONDARY_LINE_TEXT_SPACING_MM;

      // Logo bottom Y position for text alignment
      const logoBottomY = logoBottomRightY + TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM;

      // Draw line
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(3, 125, 232);
      this.pdf.line(lineBottomRightXStart, logoBottomRightCenterY, lineBottomRightXEnd, logoBottomRightCenterY);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);

      // Draw text (aligned to bottom of logo)
      this.pdf.setTextColor(3, 125, 232);
      this.pdf.text('Powered by', textX, logoBottomY);

      // Draw secondary logo — only rendered when the pre-loaded data URL is
      // available; never fall back to the raw path string, which would cause
      // jspdf to throw "wrong PNG signature" on the raw bytes.
      if (this.footerLogo) {
        this.pdf.addImage(
          this.footerLogo,
          IMAGE_SETTINGS.PNG_FORMAT,
          logoBottomRightX,
          logoBottomRightY,
          TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_WIDTH_MM,
          TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM
        );
      }
    } else {
      // Bottom left position — only rendered when the logo loaded successfully as a data URL.
      if (coverLogoSrc) {
        this.pdf.addImage(
          coverLogoSrc,
          coverLogoFormat,
          TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM,
          pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - logoHeightMm / 2,
          TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM,
          TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM
        );
      }

      // Draw a horizontal line to the right of the logo, aligned at the bottom of the logo, spanning to page margin
      const logoY = pageHeight - TEMPLATE_CONSTANTS.COVER_VERTICAL_MARGIN_MM - logoHeightMm / 2;
      const logoBottomY = logoY + TEMPLATE_CONSTANTS.COVER_LOGO_HEIGHT_MM;
      const lineXStart =
        TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM +
        TEMPLATE_CONSTANTS.COVER_LOGO_WIDTH_MM +
        TEMPLATE_CONSTANTS.COVER_LINE_LEFT_MARGIN_MM;
      const lineXEnd = pageWidth - TEMPLATE_CONSTANTS.COVER_HORIZONTAL_MARGIN_MM;
      this.pdf.setLineWidth(0.4);
      this.pdf.setDrawColor(255, 255, 255);
      this.pdf.line(lineXStart, logoBottomY, lineXEnd, logoBottomY);
      this.pdf.setDrawColor(0, 0, 0);
      this.pdf.setLineWidth(0.2);
    }

    const normalizedMeasurementDate = measurementDate ? extractDate({ dateSlug: measurementDate }) : null;
    const parsedMeasurementDate = normalizedMeasurementDate
      ? moment(normalizedMeasurementDate, ISO_DATE_FORMAT, true)
      : null;
    const hasValidMeasurementDate = Boolean(parsedMeasurementDate?.isValid());

    const halfHeight = this.pdf.internal.pageSize.getHeight() / 2;
    const xPosition = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const coverTitleY = halfHeight - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const entityNameY = halfHeight + TEMPLATE_CONSTANTS.COVER_TEXT_SEPARATOR_MM;
    const measurementDateY = entityNameY + 14;
    const dateOfIssueY = hasValidMeasurementDate
      ? measurementDateY + 10
      : halfHeight + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

    this.pdf.setTextColor(isFirmOwned ? 0 : 255, isFirmOwned ? 0 : 255, isFirmOwned ? 0 : 255);

    // Color for accent elements (line and dateOfIssue): #328E65 if isFirmOwned, otherwise #31EDA9
    const accentColor = isFirmOwned ? [50, 142, 101] : [49, 237, 169];

    this.pdf.setFont('Inter', 'italic');
    this.pdf.setFontSize(16);
    this.pdf.text(`${coverTitle}`, xPosition, coverTitleY);

    // Draw a line to the right of the coverTitle, aligned at the bottom
    const coverTitleWidth = this.pdf.getTextWidth(`${coverTitle}`);
    const lineXStart = xPosition + coverTitleWidth;
    const lineXEnd = lineXStart + TEMPLATE_CONSTANTS.COVER_TITLE_LINE_LENGTH_MM;
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(accentColor[0], accentColor[1], accentColor[2]);
    this.pdf.line(lineXStart, coverTitleY, lineXEnd, coverTitleY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    this.pdf.setFont('Neuton', 'light');
    this.pdf.setFontSize(40);
    this.pdf.text(`${entityName}`, xPosition, entityNameY);

    if (hasValidMeasurementDate && parsedMeasurementDate) {
      const measurementDateText = `Valuation Date: ${parsedMeasurementDate.format('MMMM D, YYYY')}`;
      this.pdf.setFont('Inter', 'normal');
      this.pdf.setFontSize(17.5);
      this.pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
      this.pdf.text(measurementDateText, xPosition, measurementDateY);
    }

    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(10);
    this.pdf.setTextColor(accentColor[0], accentColor[1], accentColor[2]);
    this.pdf.text(`${dateOfIssue}`.toUpperCase(), xPosition, dateOfIssueY);

    // Set page text color to #037DE8
    this.pdf.setTextColor(0, 3, 125, 232);
  }

  /**
   * Generate header template for a PDF page
   */
  public generateHeader(config: PdfExportConfig, pageTitle: string): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const { entityName } = config;
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const headerY = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;

    // Logo dimensions: 75px width, 25px height converted to mm
    const logoWidthMm = 75 / CONVERSION_RATIOS.MM_TO_PX; // ~19.84mm
    const logoHeightMm = 25 / CONVERSION_RATIOS.MM_TO_PX; // ~6.62mm

    // Position logo at left margin, vertically centered
    const logoX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const logoY = headerY - logoHeightMm / 2;

    // Add page logo
    if (this.pageLogo) {
      this.pdf.addImage(
        this.pageLogo,
        PdfExporter.getImageFormat(this.pageLogo),
        logoX,
        logoY,
        logoWidthMm,
        logoHeightMm
      );
    }

    // Set font properties for entityName measurement: Inter, Bold (700), 10px
    // Try to use Inter Bold, fallback to Inter Regular if not available

    this.pdf.setFont('Inter', 'bold');
    this.pdf.setFontSize(10);

    // Text transform: uppercase
    const entityNameText = (entityName || '').toUpperCase();
    const entityNameWidth = this.pdf.getTextWidth(entityNameText);

    // Entity name positioned at right margin
    const entityNameSpacingMm = 3; // Space between line and entityName
    const entityNameX = pageWidth - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const entityNameY = headerY;

    // Line after logo, ending before entityName
    const lineSpacingMm = 3; // Space between logo and line
    const lineXStart = logoX + logoWidthMm + lineSpacingMm;
    const lineXEnd = entityNameX - entityNameSpacingMm - entityNameWidth;
    const lineY = headerY;

    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(3, 125, 232); // #037DE8
    this.pdf.line(lineXStart, lineY, lineXEnd, lineY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Draw entity name
    this.pdf.setTextColor(3, 125, 232); // #037DE8
    this.pdf.text(entityNameText, entityNameX, entityNameY, { align: 'right' });

    // Add page title below the header
    const titleSpacingBelowHeaderMm = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2;
    const titleY = headerY + titleSpacingBelowHeaderMm;
    this.pdf.setFont('Neuton', 'italic');
    this.pdf.setFontSize(20);
    this.pdf.text(pageTitle || '', TEMPLATE_CONSTANTS.PAGE_MARGIN_MM, titleY, { align: 'left' });

    // Draw line below page title
    const titleLineWidthPx = 50;
    const titleLineHeightPx = 2;
    const titleLineWidthMm = titleLineWidthPx / CONVERSION_RATIOS.MM_TO_PX;
    const titleLineHeightMm = titleLineHeightPx / CONVERSION_RATIOS.MM_TO_PX;
    const titleLineSpacingMm = 2; // Space between title and line
    const titleLineY = titleY + titleLineSpacingMm;
    const titleLineX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

    this.pdf.setLineWidth(titleLineHeightMm);
    this.pdf.setDrawColor(49, 237, 169); // #31EDA9
    this.pdf.line(titleLineX, titleLineY, titleLineX + titleLineWidthMm, titleLineY);
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);

    // Reset text color
    this.pdf.setTextColor(0, 0, 0);
  }

  /**
   * Generate footer template for a PDF page
   * Layout: firmName (or "Powered By" + logo) + line + status box + line + page number info
   */
  public generateFooter(
    currentPage: number,
    totalPages: number,
    firmName: string,
    status: number | undefined,
    isFirmOwned: boolean
  ): void {
    if (!this.pdf) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const pageWidth = this.pdf.internal.pageSize.getWidth();
    const pageHeight = this.pdf.internal.pageSize.getHeight();

    // Footer positioned at the bottom of the page
    const footerYMm = pageHeight - 15;
    const footerBaselineY = footerYMm;

    // Color constants
    const blueColor = [3, 125, 232]; // #037DE8

    // Determine status box background color based on status
    // Convert hex with alpha to RGB (approximate with lighter colors)
    let statusBoxBgColor: number[];

    if (status === 0) {
      // PUBLISHED: #19C2E71A (cyan/blue with alpha)
      statusBoxBgColor = [230, 248, 255]; // Light cyan to approximate rgba(25, 194, 231, 0.1)
    } else if (status === 1) {
      // FINAL: no fill
      statusBoxBgColor = [255, 255, 255];
    } else {
      // DRAFT: #7777771A (grey with alpha)
      statusBoxBgColor = [245, 245, 245]; // Light grey to approximate rgba(119, 119, 119, 0.1)
    }

    // Text color is the same for all statuses
    const statusTextColor = [0, 74, 139]; // #004A8B (dark blue)

    // Font settings - all text uses same font and size
    const fontSize = 10;
    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(fontSize);

    // 1. Draw firmName or "Powered By" + logo on the left
    let leftElementWidth = 0;
    const leftElementX = TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const leftElementY = footerBaselineY + 1.5;

    if (isFirmOwned) {
      // Draw firmName
      const firmNameText = (firmName || '').toUpperCase();
      this.pdf.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
      this.pdf.text(firmNameText, leftElementX, leftElementY);
      leftElementWidth = this.pdf.getTextWidth(firmNameText);
    } else {
      // Draw "Powered By" + logo
      this.pdf.setFont('Inter', 'extra-light');
      this.pdf.setFontSize(10);
      this.pdf.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
      const poweredByText = 'Powered By';
      const poweredByWidth = this.pdf.getTextWidth(poweredByText);
      const textLogoSpacing = 2; // Space between text and logo

      // Draw "Powered By" text
      this.pdf.text(poweredByText, leftElementX, leftElementY);

      // Draw scalar logo next to text
      if (this.footerLogo) {
        const logoWidthMm = TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_WIDTH_MM;
        const logoHeightMm = TEMPLATE_CONSTANTS.COVER_SECONDARY_LOGO_HEIGHT_MM;
        const logoX = leftElementX + poweredByWidth + textLogoSpacing;
        const logoY = footerBaselineY - logoHeightMm / 2;

        this.pdf.addImage(this.footerLogo, IMAGE_SETTINGS.PNG_FORMAT, logoX, logoY, logoWidthMm, logoHeightMm);

        leftElementWidth = poweredByWidth + textLogoSpacing + logoWidthMm;
      } else {
        leftElementWidth = poweredByWidth;
      }

      // Reset font for rest of footer
      this.pdf.setFont('Inter', 'normal');
    }

    // Calculate positions for elements
    const elementSpacing = 3; // Space between elements
    const line1StartX = leftElementX + leftElementWidth + elementSpacing;

    // Determine status text
    let statusText = 'DRAFT - FOR DISCUSSION PURPOSE ONLY - SUBJECT TO CHANGE';

    if (status === 0) {
      // ALLOCATIONS_PUBLISHED
      statusText = 'PUBLISHED - SUBJECT TO CHANGE';
    }
    if (status === 1) {
      // ALLOCATIONS_FINAL
      statusText = 'FINAL';
    }

    // Calculate status box dimensions
    // Height: 24px converted to mm
    const statusBoxHeightMm = 24 / CONVERSION_RATIOS.MM_TO_PX; // Approximately 6.35mm

    const iconSize = 2.5; // Icon size in mm
    const iconPadding = 1.5; // Padding around icon
    const statusBoxPadding = 2; // Padding inside the box (horizontal)

    // Calculate text width for status text
    const statusTextWidth = this.pdf.getTextWidth(statusText);

    // Determine if icon should be shown (only for draft)
    const showIcon = status === -1 || status === undefined;

    // Calculate content width (icon + spacing + text)
    const contentWidth = showIcon ? iconSize + iconPadding + statusTextWidth : statusTextWidth;

    // Status box width = content + padding on both sides
    const statusBoxWidth = contentWidth + statusBoxPadding * 2;

    // Calculate page number text and position first to determine line positions
    const pageNumberText = `PAGE ${currentPage} OF ${totalPages}`;
    this.pdf.setFont('Inter', 'normal');
    this.pdf.setFontSize(fontSize);
    this.pdf.setTextColor(blueColor[0], blueColor[1], blueColor[2]);
    const pageNumberWidth = this.pdf.getTextWidth(pageNumberText);
    const pageNumberX = pageWidth - TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;
    const pageNumberY = footerBaselineY;

    // Calculate line lengths - longer when status is not draft (closer to status box)
    const availableWidth = pageNumberX - pageNumberWidth - line1StartX - elementSpacing * 4; // Account for spacing
    const isDraft = status === -1 || status === undefined;

    // For non-draft statuses, make lines longer (closer to status box)
    // For draft, keep lines shorter
    const lineLength = isDraft
      ? Math.min(availableWidth / 3, 30) // Shorter for draft
      : Math.min(availableWidth / 2.5, 40); // Longer for published/final

    // Position first line end
    const line1EndX = line1StartX + lineLength;

    // Position second line start (before page number)
    const line2EndX = pageNumberX - pageNumberWidth - elementSpacing;
    const line2StartX = line2EndX - lineLength;

    // Center status box between the two lines
    const statusBoxCenterX = (line1EndX + line2StartX) / 2;
    const statusBoxX = statusBoxCenterX - statusBoxWidth / 2;
    const statusBoxY = footerBaselineY - statusBoxHeightMm / 2;

    // 2. Draw first blue line (from firmName, shorter length)
    this.pdf.setLineWidth(0.4);
    this.pdf.setDrawColor(blueColor[0], blueColor[1], blueColor[2]);
    this.pdf.line(line1StartX, footerBaselineY, line1EndX, footerBaselineY);

    // 3. Draw status box with background
    // Draw background rectangle with rounded corners
    this.pdf.setFillColor(statusBoxBgColor[0], statusBoxBgColor[1], statusBoxBgColor[2]);
    this.pdf.roundedRect(statusBoxX, statusBoxY, statusBoxWidth, statusBoxHeightMm, 1.5, 1.5, 'F');

    // Calculate centered positions for icon and text
    const contentStartX = statusBoxX + (statusBoxWidth - contentWidth) / 2; // Center content horizontally

    // Add info icon inside the box (only for draft, aligned with text baseline)
    if (showIcon && this.infoIcon) {
      const iconX = contentStartX;
      // Align icon center with text baseline (footerBaselineY)
      const iconY = footerBaselineY - iconSize / 2;
      this.pdf.addImage(this.infoIcon, IMAGE_SETTINGS.PNG_FORMAT, iconX, iconY, iconSize, iconSize);
    }

    // Add status text inside the box (centered horizontally, aligned with baseline)
    this.pdf.setTextColor(statusTextColor[0], statusTextColor[1], statusTextColor[2]);
    const statusTextX = showIcon ? contentStartX + iconSize + iconPadding : contentStartX;
    // Text baseline should align with footerBaselineY for proper vertical centering
    const statusTextY = footerBaselineY + 1.5;
    this.pdf.text(statusText, statusTextX, statusTextY);

    // Draw second blue line (shorter length, before page number)
    this.pdf.line(line2StartX, footerBaselineY, line2EndX, footerBaselineY);

    // 5. Draw page number info on the right
    this.pdf.text(pageNumberText, pageNumberX, pageNumberY, { align: 'right' });

    // Reset drawing settings
    this.pdf.setDrawColor(0, 0, 0);
    this.pdf.setLineWidth(0.2);
    this.pdf.setTextColor(0, 0, 0);
  }

  /**
   * Phase 1 of streaming export: create the JsPDF instance, load fonts and
   * static logos, and render the cover. Must be called before addCapturedPage.
   */
  public async initDocument(config: PdfExportConfig): Promise<void> {
    this.renderConfig = config;
    this.addedPages = [];

    this.pdf = new JsPDF({
      format: 'letter',
      orientation: 'landscape',
      compress: true,
    });

    await PdfExporter.loadCustomFonts(this.pdf);

    // Load each image independently so a single failure does not prevent the
    // others from being available. All fields default to null and callers fall
    // back to the raw source path when null.
    const loadResult = await Promise.allSettled([
      PdfExporter.loadImageAsDataUrl(config.pageLogo),
      PdfExporter.loadImageAsDataUrl(config.coverLogo),
      PdfExporter.loadImageAsDataUrl(config.infoIcon),
      PdfExporter.loadImageAsDataUrl(config.footerLogo),
    ]);
    if (loadResult[0].status === 'fulfilled') this.pageLogo = loadResult[0].value;
    else Logger.warn('Failed loading pageLogo for PDF export');
    if (loadResult[1].status === 'fulfilled') this.coverLogoDataUrl = loadResult[1].value;
    else Logger.warn('Failed loading coverLogo for PDF export');
    if (loadResult[2].status === 'fulfilled') this.infoIcon = loadResult[2].value;
    else Logger.warn('Failed loading infoIcon for PDF export');
    if (loadResult[3].status === 'fulfilled') this.footerLogo = loadResult[3].value;
    else Logger.warn('Failed loading footerLogo for PDF export');

    await this.generateCover(config);
  }

  /**
   * Phase 2 of streaming export: add a single captured page to the PDF.
   * Footer rendering is deferred to finalize() because the "Page X of Y"
   * stamp needs the final included-page count.
   *
   * The image's Blob is converted to a data URL only at the moment of
   * addImage, then dropped. This keeps peak heap usage to a single decoded
   * image, regardless of how many pages have been captured.
   */
  public async addCapturedPage(link: PdfPage, image: CapturedImage): Promise<void> {
    if (!this.pdf || !this.renderConfig) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    if (image.width <= 0 || image.height <= 0) {
      Logger.warn(`addCapturedPage: skipping ${link.exportTarget} — image has zero dimensions`);
      return;
    }

    const config = this.renderConfig;
    const pageNumberInExport = this.addedPages.length + 1;

    this.pdf.addPage();

    this.pdf.addImage(
      config.pageBackgroundImage,
      IMAGE_SETTINGS.PNG_FORMAT,
      0,
      0,
      this.pdf.internal.pageSize.getWidth(),
      this.pdf.internal.pageSize.getHeight(),
      'page-background-image'
    );

    this.generateHeader(config, link.title);

    // Decode the Blob only at the moment of addImage, so the data URL string
    // exists transiently rather than accumulating across all captures.
    const contentImgData = await PdfExporter.blobToDataUrl(image.blob);
    const contentImgFormat = image.format;

    const canvasWidthMm = image.width / CONVERSION_RATIOS.MM_TO_PX;
    const canvasHeightMm = image.height / CONVERSION_RATIOS.MM_TO_PX;
    // html2canvas captures at scale:2, so 0.5 maps back to 1 CSS px = 1 PDF unit at 96 DPI.
    // Capping at 0.5 prevents small tables (e.g. External Valuation) from being upscaled
    // beyond their natural browser size while leaving large downscaled tables unchanged.
    const NATURAL_SCALE_CAP = 0.5;
    const contentScale = Math.min(
      IMAGE_SETTINGS.MAX_WIDTH_MM / canvasWidthMm,
      IMAGE_SETTINGS.MAX_HEIGHT_MM / canvasHeightMm,
      NATURAL_SCALE_CAP
    );
    const contentWidthMm = canvasWidthMm * contentScale;
    const contentHeightMm = canvasHeightMm * contentScale;

    const dimensionsAreValid =
      Number.isFinite(contentWidthMm) &&
      Number.isFinite(contentHeightMm) &&
      contentWidthMm > 0 &&
      contentHeightMm > 0;

    const contentXPositionMm =
      TEMPLATE_CONSTANTS.PAGE_MARGIN_MM + (IMAGE_SETTINGS.MAX_WIDTH_MM - contentWidthMm) / 2;
    const contentYPositionMm =
      TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM;

    let contentAdded = false;
    if (dimensionsAreValid) {
      try {
        this.pdf.addImage(
          contentImgData,
          contentImgFormat,
          contentXPositionMm,
          contentYPositionMm,
          contentWidthMm,
          contentHeightMm,
          IMAGE_SETTINGS.PDF_ALIAS,
          IMAGE_SETTINGS.COMPRESSION
        );
        contentAdded = true;
        Logger.info(`Added content to page ${pageNumberInExport}`);
      } catch (imgError) {
        Logger.warn(`addImage failed on page ${pageNumberInExport}; falling back to placeholder`, imgError);
      }
    } else {
      Logger.warn(
        `Skipping addImage on page ${pageNumberInExport}: computed dimensions invalid ` +
          `(w=${contentWidthMm}, h=${contentHeightMm}, image=${image.width}x${image.height})`
      );
    }

    if (!contentAdded) {
      this.pdf.setFont('Inter', 'italic');
      this.pdf.setFontSize(14);
      this.pdf.setTextColor(128, 128, 128);
      this.pdf.text(
        'No data available',
        TEMPLATE_CONSTANTS.PAGE_MARGIN_MM,
        TEMPLATE_CONSTANTS.HEADER_TOTAL_HEIGHT_MM + TEMPLATE_CONSTANTS.PAGE_MARGIN_MM * 2
      );
      this.pdf.setTextColor(0, 0, 0);
    }

    this.addedPages.push({ link });
  }

  /**
   * Phase 3 of streaming export: stamp footers (with final "Page X of Y"
   * counts), output the blob, and optionally trigger the browser download.
   */
  public async finalize(filename: string, triggerDownload: boolean): Promise<PdfExportResult> {
    if (!this.pdf || !this.renderConfig) {
      throw new Error('PDF instance not initialized. Call initDocument() first.');
    }
    const config = this.renderConfig;
    const totalIncludedPages = this.addedPages.length;

    Logger.info(
      `Finalizing PDF with ${totalIncludedPages} page(s) of content`
    );

    // Two-pass footer stamping. PDF page 1 is the cover; captured pages start
    // at PDF page 2. setPage(n) activates page n for subsequent drawing.
    for (let i = 0; i < totalIncludedPages; i++) {
      this.pdf.setPage(i + 2);
      this.generateFooter(
        i + 1,
        totalIncludedPages,
        config.firmName,
        config.allocationStatus,
        config.isFirmOwned
      );
    }

    const safeFilename = filename || 'pdf-export';
    const pdfBlob = this.pdf.output('blob');

    if (triggerDownload) {
      Logger.info(`Downloading final PDF: ${safeFilename}.pdf`);
      try {
        this.pdf.save(`${safeFilename}.pdf`);
      } catch (saveError) {
        Logger.error('Error calling finalPdf.save():', saveError);
      }
    }

    return {
      success: true,
      filename: `${safeFilename}.pdf`,
      totalPages: totalIncludedPages,
      processedPages: totalIncludedPages,
      pdfBlob,
    };
  }

  private static blobToDataUrl(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }
}
